/**
 * 統合タイムラインビュー
 *
 * 読書履歴、Garminアクティビティ、Overland位置データを時系列で表示
 * 選択された日付のデータのみ表示
 */

import { format, startOfDay } from 'date-fns'
import { Book, Clock, Dumbbell, MapPin, NotebookPen, Plus, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { getImmichAssetsByDate, type ImmichAsset } from '@/api/services/immich'
import { getTimelineNotes } from '@/api/services/overland'
import type { ReadHistory } from '@/api/type'
import type { GarminActivitySummary, GarminSleepRaw } from '@/api/types/garmin'
import type { OverlandVisit, TimelineNote } from '@/api/types/overland'
import { OverlandMap } from '@/components/shared/map/overland-map'
import { Button } from '@/components/ui/button'
import { Fab } from '@/components/ui/fab'
import { Skeleton } from '@/components/ui/skeleton'
import { isSameDate } from '@/lib/date-utils'
import { listSleepEpisodes } from '@/lib/garmin-sleep'
import {
  filterLocationsByTimeRange,
  filterVisitsByTimeRange,
  isFullDayRange,
  type MapTimeRange,
} from '@/lib/map-time-filter'
import type { MovementSessionWithVisits } from '@/lib/movement-session'
import {
  buildVisitCardTransactionMap,
  buildVisitReceiptMap,
  listUnassignedSpendings,
  type UnassignedSpending,
} from '@/lib/receipt-visit-utils'
import { getVisitTimelineTimestamp } from '@/lib/visit-day-utils'
import { useUnmatchedCardTransactions } from '@/pages/receipts/hooks/use-card-transactions'
import { useReceipts } from '@/pages/receipts/hooks/use-receipts'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'
import { useOverlandStore } from '@/store/useOverlandStore'
import { useProgressStore } from '@/store/useProgressStore'

import { DAILY_TIMELINE_TYPES, useTimelineItemFilter } from '../timeline-item-filters'
import { CreateVisitSheet } from './activity-card/create-visit-sheet'
import { GarminActivityCard } from './activity-card/garmin-activity-card'
import { MovementDetailSheet } from './activity-card/movement-detail-sheet'
import { NoteInputSheet } from './activity-card/note-input-sheet'
import { OverlandMovementCard } from './activity-card/overland-movement-card'
import { OverlandVisitCard } from './activity-card/overland-visit-card'
import { PhotoGroupCard } from './activity-card/photo-group-card'
import { PlaceDetailSheet } from './activity-card/place-detail-sheet'
import { PlaceEditSheet } from './activity-card/place-edit-sheet'
import { ReadCard } from './activity-card/read-card'
import { ReviewEventRow } from './activity-card/review-event-row'
import { SleepCard } from './activity-card/sleep-card'
import { SpendingRow } from './activity-card/spending-row'
import { TimelineNoteCard } from './activity-card/timeline-note-card'
import { MapTimeFilterBar } from './map-time-filter-bar'
import { SpendingSummary } from './spending-summary'
import { StepsSummary } from './steps-timeline'
import { TimelineItemFilterChips } from './timeline-item-filter-chips'
import { TripDayCards } from './trip-day-cards'

type Props = {
  history: ReadHistory[]
  isLoading: boolean
  selectedDate: Date
  // 一括編集モード用
  isSelectMode?: boolean
  selectedVisitIds?: Set<number>
  onToggleVisitSelect?: (visitId: number) => void
  onRefreshReady?: (refreshFn: (() => void) | null) => void
}

// 統一されたタイムラインアイテム
type TimelineItem =
  | { type: 'read'; data: ReadHistory; timestamp: Date }
  | { type: 'garmin'; data: GarminActivitySummary; timestamp: Date }
  | { type: 'visit'; data: OverlandVisit; timestamp: Date }
  | { type: 'movement'; data: MovementSessionWithVisits; timestamp: Date }
  | { type: 'sleep'; data: GarminSleepRaw; timestamp: Date }
  | { type: 'note'; data: TimelineNote; timestamp: Date }
  | { type: 'photos'; data: ImmichAsset[]; timestamp: Date }
  | { type: 'spending'; data: UnassignedSpending; timestamp: Date }

const MAP_HEIGHT_STORAGE_KEY = 'activity-map-height'
const MAP_HEIGHT_MIN = 150
const MAP_HEIGHT_MAX = 1200
const MAP_TIME_FILTER_BAR_ID = 'map-time-filter-bar'

const getStoredMapHeight = (): number => {
  const stored = localStorage.getItem(MAP_HEIGHT_STORAGE_KEY)
  if (stored) {
    const num = Number(stored)
    if (!Number.isNaN(num) && num >= MAP_HEIGHT_MIN && num <= MAP_HEIGHT_MAX) return num
  }
  return 300
}

export const TimelineView = ({
  history,
  isLoading,
  selectedDate,
  isSelectMode = false,
  selectedVisitIds = new Set(),
  onToggleVisitSelect,
  onRefreshReady,
}: Props) => {
  const { removeReadHistory } = useProgressStore()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const [, setSearchParams] = useSearchParams()
  const { activities, fetchActivities, isActivitiesLoading, steps15Min, fetchSteps15Min, sleep, fetchSleep } =
    useGarminStore()
  const { locations, visits, movementSessions, fetchVisits, fetchLocations, isVisitsLoading, isLocationsLoading } =
    useOverlandStore()

  // タイムラインに出す項目の絞り込み（初期値は全表示）
  const { hiddenTypes, isVisible, toggleType } = useTimelineItemFilter()

  // マップサイズ（ドラッグリサイズ）
  const [mapHeight, setMapHeight] = useState(getStoredMapHeight)
  const mapHeightRef = useRef(mapHeight)
  useEffect(() => {
    mapHeightRef.current = mapHeight
  }, [mapHeight])
  const dragStartRef = useRef<{ y: number; height: number } | null>(null)

  const handleDragStart = useCallback((clientY: number) => {
    dragStartRef.current = { y: clientY, height: mapHeightRef.current }
  }, [])

  const handleDragMove = useCallback((clientY: number) => {
    if (!dragStartRef.current) return
    const delta = clientY - dragStartRef.current.y
    const newHeight = Math.min(MAP_HEIGHT_MAX, Math.max(MAP_HEIGHT_MIN, dragStartRef.current.height + delta))
    setMapHeight(newHeight)
  }, [])

  const handleDragEnd = useCallback(() => {
    dragStartRef.current = null
    localStorage.setItem(MAP_HEIGHT_STORAGE_KEY, String(mapHeightRef.current))
  }, [])

  // ドラッグハンドラーをrefで安定化（document.addEventListenerで使用するため）
  const handlersRef = useRef({ handleDragMove, handleDragEnd })
  useEffect(() => {
    handlersRef.current = { handleDragMove, handleDragEnd }
  }, [handleDragMove, handleDragEnd])

  // マウスイベント
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      handleDragStart(e.clientY)
      const onMouseMove = (ev: MouseEvent) => handlersRef.current.handleDragMove(ev.clientY)
      const onMouseUp = () => {
        handlersRef.current.handleDragEnd()
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [handleDragStart],
  )

  // タッチイベント
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      handleDragStart(touch.clientY)
    },
    [handleDragStart],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      e.preventDefault()
      handleDragMove(touch.clientY)
    },
    [handleDragMove],
  )

  const handleTouchEnd = useCallback(() => {
    handleDragEnd()
  }, [handleDragEnd])

  // タイムラインメモ
  const [timelineNotes, setTimelineNotes] = useState<TimelineNote[]>([])

  // Immich 写真
  const [photos, setPhotos] = useState<ImmichAsset[]>([])

  // メモ入力シート
  const [noteSheetOpen, setNoteSheetOpen] = useState(false)
  const [noteSheetTimestamp, setNoteSheetTimestamp] = useState<string>(new Date().toISOString())
  const [noteSheetVisitId, setNoteSheetVisitId] = useState<number | null>(null)
  const [noteSheetEpisodeId, setNoteSheetEpisodeId] = useState<number | null>(null)
  const [noteSheetTitle, setNoteSheetTitle] = useState('メモを追加')

  // タイムラインメモを取得
  const fetchNotes = useCallback(async () => {
    if (!hasFullAccess) return
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    try {
      const notes = await getTimelineNotes(dateStr)
      setTimelineNotes(notes)
    } catch {
      // エラー時は空配列のまま
    }
  }, [hasFullAccess, selectedDate])

  useEffect(() => {
    void fetchNotes()
  }, [fetchNotes])

  // Immich 写真取得 (フルアクセスユーザーのみ)
  useEffect(() => {
    if (!hasFullAccess) {
      setPhotos([])
      return
    }
    let cancelled = false
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    getImmichAssetsByDate(dateStr)
      .then((assets) => {
        if (!cancelled) setPhotos(assets)
      })
      .catch(() => {
        if (!cancelled) setPhotos([])
      })
    return () => {
      cancelled = true
    }
  }, [hasFullAccess, selectedDate])

  // フルアクセスユーザーの場合、Garminアクティビティを取得
  useEffect(() => {
    if (hasFullAccess) {
      fetchActivities(30) // 最新30件のアクティビティ
    }
  }, [hasFullAccess, fetchActivities])

  // Overland訪問データと位置データを取得
  useEffect(() => {
    if (!hasFullAccess) return

    let cancelled = false
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    // 訪問を先に取得してから位置データを取得（移動セッション生成時に訪問で分割するため）
    void fetchVisits(dateStr)
      .then(() => {
        if (!cancelled) return fetchLocations(dateStr)
      })
      .catch(() => {
        // 訪問取得失敗時も位置データは取得を試みる
        if (!cancelled) void fetchLocations(dateStr)
      })
    fetchSteps15Min(dateStr) // 選択日の15分刻み歩数
    fetchSleep(dateStr) // 選択日の睡眠データ

    return () => {
      cancelled = true
    }
  }, [hasFullAccess, selectedDate, fetchVisits, fetchLocations, fetchSteps15Min, fetchSleep])

  // モーション更新後にデータをリロード（silent: 全体ローディングを出さない）
  const handleMotionUpdated = useCallback(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    fetchLocations(dateStr, undefined, { silent: true })
  }, [selectedDate, fetchLocations])

  // 訪問更新後にデータをリロード（silent fallback）
  // 通常はシートが store の upsertVisit/removeVisit を直接呼ぶため、refetch は不要だが
  // 一括 merge など複雑な操作のフォールバック用に残す
  const handleVisitUpdated = useCallback(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    fetchVisits(dateStr, undefined, { silent: true })
  }, [selectedDate, fetchVisits])

  // 地図からのVisitクリック用の状態（詳細シート）
  const [mapSelectedVisit, setMapSelectedVisit] = useState<OverlandVisit | null>(null)
  // 詳細シートから編集シートへの遷移用
  const [mapEditVisit, setMapEditVisit] = useState<OverlandVisit | null>(null)

  // 地図でVisitがクリックされたとき
  const handleMapVisitClick = useCallback((visit: OverlandVisit) => {
    setMapSelectedVisit(visit)
  }, [])

  // 詳細シートから編集ボタンがクリックされたとき
  const handleDetailEditClick = useCallback(() => {
    if (mapSelectedVisit) {
      setMapEditVisit(mapSelectedVisit)
      setMapSelectedVisit(null)
    }
  }, [mapSelectedVisit])

  // Visit作成用の状態（地図ポリラインクリックから）
  const [createVisitPoint, setCreateVisitPoint] = useState<{
    lat: number
    lng: number
    timestamp: string
  } | null>(null)

  // 移動セッション詳細シート用の状態
  const [selectedMovement, setSelectedMovement] = useState<MovementSessionWithVisits | null>(null)

  // マップハイライト用（シートを開いている間ハイライト維持）
  const [highlightedSession, setHighlightedSession] = useState<MovementSessionWithVisits | null>(null)

  // 地図でポリラインクリック → Visit作成要求
  const handleCreateVisit = useCallback((lat: number, lng: number, timestamp: string) => {
    setCreateVisitPoint({ lat, lng, timestamp })
  }, [])

  // 地図の時間フィルタ
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd')
  const [mapTimeRange, setMapTimeRange] = useState<MapTimeRange | null>(null)
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false)

  // 日付を切り替えたら絞り込みを解除
  useEffect(() => {
    setMapTimeRange(null)
  }, [selectedDateStr])

  const isMapFiltered = mapTimeRange !== null && !isFullDayRange(mapTimeRange)
  const mapLocations = useMemo(
    () => filterLocationsByTimeRange(locations, selectedDateStr, mapTimeRange),
    [locations, selectedDateStr, mapTimeRange],
  )
  const mapVisits = useMemo(
    () => filterVisitsByTimeRange(visits, selectedDateStr, mapTimeRange),
    [visits, selectedDateStr, mapTimeRange],
  )

  // タイムラインで移動カードクリック
  const handleMovementClick = useCallback((session: MovementSessionWithVisits) => {
    setSelectedMovement(session)
    setHighlightedSession(session)
  }, [])

  // 睡眠カードクリック → ヘルスタブに遷移
  const handleSleepClick = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.set('tab', 'health')
      return newParams
    })
  }, [setSearchParams])

  // 移動詳細シートを閉じたときにハイライトも解除
  const handleMovementSheetChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedMovement(null)
      setHighlightedSession(null)
    }
  }, [])

  // 親コンポーネントにリフレッシュ関数を渡す（silent refetch）
  useEffect(() => {
    onRefreshReady?.(handleVisitUpdated)
    return () => onRefreshReady?.(null)
  }, [onRefreshReady, handleVisitUpdated])

  // タイムラインメモのローカル更新ヘルパー（API は子コンポーネントが呼ぶ）
  const handleNoteCreated = useCallback(
    (note: TimelineNote) => {
      // 選択日と異なる日付に作成されたメモは表示対象外（次の日付遷移時に fetchNotes される）
      if (!isSameDate(new Date(note.timestamp), selectedDate)) return
      setTimelineNotes((prev) => [...prev, note])
    },
    [selectedDate],
  )
  const handleNoteChanged = useCallback((note: TimelineNote) => {
    setTimelineNotes((prev) => prev.map((n) => (n.id === note.id ? note : n)))
  }, [])
  const handleNoteDeleted = useCallback((noteId: number) => {
    setTimelineNotes((prev) => prev.filter((n) => n.id !== noteId))
  }, [])

  // メモ入力シートを開く（FAB用）
  const handleOpenNoteSheet = useCallback(() => {
    setNoteSheetTimestamp(new Date().toISOString())
    setNoteSheetVisitId(null)
    setNoteSheetEpisodeId(null)
    setNoteSheetTitle('メモを追加')
    setNoteSheetOpen(true)
  }, [])

  // メモ入力シートを開く（時間区切り用）
  const handleOpenNoteSheetAtHour = useCallback(
    (hour: number) => {
      const dt = new Date(selectedDate)
      dt.setHours(hour, 0, 0, 0)
      setNoteSheetTimestamp(dt.toISOString())
      setNoteSheetVisitId(null)
      setNoteSheetEpisodeId(null)
      setNoteSheetTitle(`${hour}:00 のメモ`)
      setNoteSheetOpen(true)
    },
    [selectedDate],
  )

  // メモ入力シートを開く（Visitカード用）
  const handleOpenNoteSheetForVisit = useCallback((visitId: number, arrivalDate: string | null) => {
    setNoteSheetTimestamp(arrivalDate ?? new Date().toISOString())
    setNoteSheetVisitId(visitId)
    setNoteSheetEpisodeId(null)
    setNoteSheetTitle('訪問メモを追加')
    setNoteSheetOpen(true)
  }, [])

  // メモ入力シートを開く（ReadCard用）
  const handleOpenNoteSheetForEpisode = useCallback((episodeId: number, startedAt: string) => {
    setNoteSheetTimestamp(startedAt)
    setNoteSheetVisitId(null)
    setNoteSheetEpisodeId(episodeId)
    setNoteSheetTitle('読書メモを追加')
    setNoteSheetOpen(true)
  }, [])

  // 選択された日付でフィルタリングした読書履歴、Garminアクティビティ、Overlandデータを統合
  const baseTimelineItems = useMemo(() => {
    const items: TimelineItem[] = []

    // 読書履歴を追加（選択日付でフィルタ）
    history.forEach((item) => {
      const itemDate = new Date(item.started_at)
      if (isSameDate(itemDate, selectedDate)) {
        items.push({
          type: 'read',
          data: item,
          timestamp: itemDate,
        })
      }
    })

    // Garminアクティビティを追加（フルアクセス＆選択日付でフィルタ）
    if (hasFullAccess && activities.length > 0) {
      activities.forEach((activity) => {
        const activityDate = new Date(activity.start_time)
        if (isSameDate(activityDate, selectedDate)) {
          items.push({
            type: 'garmin',
            data: activity,
            timestamp: activityDate,
          })
        }
      })
    }

    // Overland訪問を追加（選択日に到着したもの＋前日から続いているもの）
    if (hasFullAccess && visits.length > 0) {
      visits.forEach((visit) => {
        const visitTimestamp = getVisitTimelineTimestamp(visit, selectedDate)
        if (!visitTimestamp) return
        items.push({
          type: 'visit',
          data: visit,
          timestamp: visitTimestamp,
        })
      })
    }

    // Overland移動セッションを追加（選択日付で既にフィルタ済み）
    if (hasFullAccess && movementSessions.length > 0) {
      movementSessions.forEach((session) => {
        items.push({
          type: 'movement',
          data: session,
          timestamp: new Date(session.startTime),
        })
      })
    }

    // 睡眠データを追加（終了時刻をタイムスタンプとして使用）
    if (hasFullAccess) {
      listSleepEpisodes(sleep).forEach((episode) => {
        const end = episode.dailySleepDTO?.sleepEndTimestampGMT
        if (!end) return
        const sleepEndDate = new Date(end)
        if (isSameDate(sleepEndDate, selectedDate)) {
          items.push({ type: 'sleep', data: episode, timestamp: sleepEndDate })
        }
      })
    }

    // タイムラインメモを追加（Visitに紐付くメモはVisitカード内に表示するので除外）
    timelineNotes.forEach((note) => {
      if (note.visit_id != null) return
      items.push({
        type: 'note',
        data: note,
        timestamp: new Date(note.timestamp),
      })
    })

    // Immich 写真を Visit に割り当て、残りをクラスタリングして追加
    // (Visit の [arrival_date, departure_date] 範囲内の写真は Visit カード内で表示)
    if (photos.length > 0) {
      const PHOTO_GROUP_GAP_MS = 15 * 60 * 1000
      const dayPhotos = photos
        .filter((p) => isSameDate(new Date(p.fileCreatedAt), selectedDate))
        .sort((a, b) => new Date(a.fileCreatedAt).getTime() - new Date(b.fileCreatedAt).getTime())

      // Visit 期間の (start, end) を事前計算
      const visitRanges: { start: number; end: number }[] = []
      if (hasFullAccess) {
        visits.forEach((v) => {
          if (!v.arrival_date) return
          const start = new Date(v.arrival_date).getTime()
          // departure_date が無い (滞在中) 場合は現在時刻まで
          const end = v.departure_date ? new Date(v.departure_date).getTime() : Date.now()
          visitRanges.push({ start, end })
        })
      }
      const isInsideAnyVisit = (ts: number): boolean => visitRanges.some((r) => ts >= r.start && ts <= r.end)

      // Visit 外の写真だけをクラスタリング
      const unassigned = dayPhotos.filter((p) => !isInsideAnyVisit(new Date(p.fileCreatedAt).getTime()))

      let currentGroup: ImmichAsset[] = []
      let lastTs = 0
      const flush = () => {
        if (currentGroup.length === 0) return
        const first = currentGroup[0]
        if (!first) return
        items.push({
          type: 'photos',
          data: currentGroup,
          timestamp: new Date(first.fileCreatedAt),
        })
        currentGroup = []
      }
      unassigned.forEach((p) => {
        const ts = new Date(p.fileCreatedAt).getTime()
        if (currentGroup.length > 0 && ts - lastTs > PHOTO_GROUP_GAP_MS) {
          flush()
        }
        currentGroup.push(p)
        lastTs = ts
      })
      flush()
    }

    // 時間順にソート（古い順）
    return items.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [history, activities, visits, movementSessions, sleep, timelineNotes, photos, hasFullAccess, selectedDate])

  // Visit.id -> その Visit 期間内に撮影された写真
  const visitPhotoMap = useMemo(() => {
    const map = new Map<number, ImmichAsset[]>()
    if (!hasFullAccess || photos.length === 0 || visits.length === 0) return map
    visits.forEach((v) => {
      if (!v.arrival_date) return
      const start = new Date(v.arrival_date).getTime()
      const end = v.departure_date ? new Date(v.departure_date).getTime() : Date.now()
      const inside = photos.filter((p) => {
        const ts = new Date(p.fileCreatedAt).getTime()
        return ts >= start && ts <= end
      })
      if (inside.length > 0) map.set(v.id, inside)
    })
    return map
  }, [hasFullAccess, photos, visits])

  // Visit.id -> その滞在で買ったレシート
  const { data: dayReceipts } = useReceipts(
    { purchased_date: format(selectedDate, 'yyyy-MM-dd') },
    { enabled: hasFullAccess },
  )
  const visitReceiptMap = useMemo(() => buildVisitReceiptMap(visits, dayReceipts ?? []), [visits, dayReceipts])
  const { data: dayCardTxs } = useUnmatchedCardTransactions(selectedDateStr, { enabled: hasFullAccess })
  const visitCardTxMap = useMemo(() => buildVisitCardTransactionMap(visits, dayCardTxs ?? []), [visits, dayCardTxs])

  // 滞在に入らなかった買い物も、合計金額だけでなく行として出す
  const timelineItems = useMemo(() => {
    if (!hasFullAccess) return baseTimelineItems
    const spendings = listUnassignedSpendings(
      dayReceipts ?? [],
      visitReceiptMap,
      dayCardTxs ?? [],
      visitCardTxMap,
      startOfDay(selectedDate),
    )
    if (spendings.length === 0) return baseTimelineItems
    const spendingItems: TimelineItem[] = spendings.map((spending) => ({
      type: 'spending',
      data: spending,
      timestamp: spending.timestamp,
    }))
    return [...baseTimelineItems, ...spendingItems].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [hasFullAccess, baseTimelineItems, dayReceipts, visitReceiptMap, dayCardTxs, visitCardTxMap, selectedDate])

  const visibleTimelineItems = useMemo(
    () => timelineItems.filter((item) => !hiddenTypes.has(item.type)),
    [timelineItems, hiddenTypes],
  )

  // タイムラインアイテムに時間区切りを挿入
  const timelineWithHourSeparators = useMemo(() => {
    if (visibleTimelineItems.length === 0) return []

    const result: Array<{ type: 'hour-separator'; hour: number } | TimelineItem> = []
    let lastHour: number | null = null

    // 古い順（昇順）でソートされているので、時間が変わったら区切りを挿入
    visibleTimelineItems.forEach((item) => {
      const hour = item.timestamp.getHours()
      if (lastHour !== hour) {
        result.push({ type: 'hour-separator', hour })
        lastHour = hour
      }
      result.push(item)
    })

    return result
  }, [visibleTimelineItems])

  const isLoadingAny = isLoading || (hasFullAccess && (isActivitiesLoading || isVisitsLoading || isLocationsLoading))

  if (isLoadingAny && timelineItems.length === 0) {
    return (
      <div className="p-4 space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  // タイムラインアイテムも歩数データもない場合のみ空表示
  if (timelineItems.length === 0 && steps15Min.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4 p-8">
        <div className="flex items-center gap-2">
          <Book className="size-12 opacity-50" />
          {hasFullAccess && <Dumbbell className="size-12 opacity-50" />}
          {hasFullAccess && <MapPin className="size-12 opacity-50" />}
        </div>
        <p>この日のアクティビティはありません</p>
      </div>
    )
  }

  // Overlandデータがあるかチェック
  const hasOverlandData = hasFullAccess && (locations.length > 0 || visits.length > 0)
  const showSteps = hasFullAccess && steps15Min.length > 0 && isVisible('steps')

  return (
    <div className="space-y-4 overflow-x-hidden">
      {/* Overland地図（位置データがある場合のみ表示） */}
      {hasOverlandData && (
        <div className="relative">
          <OverlandMap
            locations={mapLocations}
            visits={mapVisits}
            height={`${mapHeight}px`}
            onMotionUpdated={handleMotionUpdated}
            onVisitClick={handleMapVisitClick}
            onCreateVisit={handleCreateVisit}
            highlightedSession={highlightedSession}
            fitKey={selectedDateStr}
          />

          {/* 絞り込み結果が空のときのメッセージ（地図はアンマウントせず表示状態を維持） */}
          {isMapFiltered && mapLocations.length === 0 && mapVisits.length === 0 && (
            <div className="absolute inset-x-0 top-12 z-1000 flex justify-center">
              <div className="bg-background/90 text-muted-foreground rounded-md border px-3 py-1.5 text-sm shadow-md backdrop-blur-sm">
                この時間帯の位置データはありません
              </div>
            </div>
          )}

          {/* 時間フィルタの表示トグル（地図右上） */}
          <div className="absolute top-2 right-2 z-1000 flex items-center gap-1">
            <Button
              variant={isMapFiltered ? 'default' : 'secondary'}
              size="sm"
              className="h-8 gap-1.5 shadow-md"
              aria-expanded={isTimeFilterOpen}
              aria-controls={MAP_TIME_FILTER_BAR_ID}
              onClick={() => setIsTimeFilterOpen((prev) => !prev)}
            >
              <Clock className="size-4" />
              {isMapFiltered && mapTimeRange ? `${mapTimeRange.start} - ${mapTimeRange.end}` : '時間'}
            </Button>
            {isMapFiltered && (
              <Button
                variant="secondary"
                size="icon"
                className="size-8 shadow-md"
                aria-label="時間の絞り込みを解除"
                onClick={() => setMapTimeRange(null)}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>

          {/* ドラッグリサイズハンドル */}
          <div
            className="flex items-center justify-center h-5 cursor-row-resize touch-none select-none hover:bg-muted/50 active:bg-muted transition-colors"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-10 h-1 rounded-full bg-muted-foreground/40" />
          </div>

          {/* 時間フィルタバー（地図の下） */}
          {isTimeFilterOpen && (
            <MapTimeFilterBar
              id={MAP_TIME_FILTER_BAR_ID}
              value={mapTimeRange}
              onChange={setMapTimeRange}
              onClose={() => setIsTimeFilterOpen(false)}
            />
          )}
        </div>
      )}

      {/* 地図からクリックされたVisit用の詳細シート */}
      {mapSelectedVisit && (
        <PlaceDetailSheet
          visit={visits.find((v) => v.id === mapSelectedVisit.id) ?? mapSelectedVisit}
          open={!!mapSelectedVisit}
          onOpenChange={(open) => !open && setMapSelectedVisit(null)}
          onEditClick={handleDetailEditClick}
        />
      )}

      {/* 詳細→編集遷移用の編集シート */}
      {mapEditVisit && (
        <PlaceEditSheet
          visit={visits.find((v) => v.id === mapEditVisit.id) ?? mapEditVisit}
          open={!!mapEditVisit}
          onOpenChange={(open) => !open && setMapEditVisit(null)}
        />
      )}

      {/* Visit作成シート（地図ポリラインクリックから） */}
      {createVisitPoint && (
        <CreateVisitSheet
          lat={createVisitPoint.lat}
          lng={createVisitPoint.lng}
          timestamp={createVisitPoint.timestamp}
          open={!!createVisitPoint}
          onOpenChange={(open) => !open && setCreateVisitPoint(null)}
        />
      )}

      {/* 表示する項目の絞り込み */}
      {hasFullAccess && (
        <TimelineItemFilterChips types={DAILY_TIMELINE_TYPES} hiddenTypes={hiddenTypes} onToggle={toggleType} />
      )}

      {/* 歩数・支出サマリー */}
      {(showSteps || (dayReceipts?.length ?? 0) > 0 || (dayCardTxs?.length ?? 0) > 0) && (
        <div className="flex items-center justify-end gap-4">
          <SpendingSummary receipts={dayReceipts ?? []} cardTransactions={dayCardTxs} />
          {showSteps && <StepsSummary steps15Min={steps15Min} />}
        </div>
      )}

      <TripDayCards date={selectedDateStr} className="px-4" />

      {/* タイムライン（時間区切り付き） */}
      <div className="space-y-2">
        {visibleTimelineItems.length === 0 && timelineItems.length > 0 && (
          <p className="text-muted-foreground py-8 text-center text-sm">選んだ項目はこの日にはありません</p>
        )}
        {timelineWithHourSeparators.map((item, index) => {
          // 時間区切り
          if (item.type === 'hour-separator') {
            return (
              <div key={`hour-${item.hour}`} className="flex items-center gap-2 py-2">
                <div className="text-sm font-medium text-muted-foreground">{item.hour}:00</div>
                <div className="flex-1 h-px bg-border" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-amber-500"
                  onClick={() => handleOpenNoteSheetAtHour(item.hour)}
                >
                  <Plus className="size-3.5" />
                </Button>
              </div>
            )
          }

          // タイムラインアイテム
          if (item.type === 'read') {
            return (
              <div key={`read-${item.data.id}`} className="relative group">
                <ReadCard
                  item={item.data}
                  onAddNote={() => handleOpenNoteSheetForEpisode(item.data.episode_id, item.data.started_at)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  onClick={() => removeReadHistory(item.data.id)}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            )
          }

          if (item.type === 'garmin') {
            return <GarminActivityCard key={`garmin-${item.data.activity_id}`} activity={item.data} />
          }

          if (item.type === 'visit') {
            return (
              <OverlandVisitCard
                key={`visit-${item.data.id}`}
                visit={item.data}
                locations={locations}
                photos={visitPhotoMap.get(item.data.id)}
                receipts={visitReceiptMap.get(item.data.id)}
                cardTransactions={visitCardTxMap.get(item.data.id)}
                onNotesUpdated={async () => {
                  await Promise.all([fetchVisits(format(selectedDate, 'yyyy-MM-dd')), fetchNotes()])
                }}
                isSelectMode={isSelectMode}
                isSelected={selectedVisitIds.has(item.data.id)}
                onToggleSelect={() => onToggleVisitSelect?.(item.data.id)}
                onAddNote={() => handleOpenNoteSheetForVisit(item.data.id, item.data.arrival_date)}
              />
            )
          }

          if (item.type === 'sleep') {
            return (
              <SleepCard
                key={`sleep-${item.data.dailySleepDTO?.sleepStartTimestampGMT ?? item.timestamp.getTime()}`}
                sleep={item.data}
                onClick={handleSleepClick}
              />
            )
          }

          if (item.type === 'note') {
            if (item.data.note_type === 'review') {
              return <ReviewEventRow key={`note-${item.data.id}`} note={item.data} />
            }
            return (
              <TimelineNoteCard
                key={`note-${item.data.id}`}
                note={item.data}
                onChanged={handleNoteChanged}
                onDeleted={handleNoteDeleted}
              />
            )
          }

          if (item.type === 'photos') {
            const firstId = item.data[0]?.id ?? 'empty'
            return <PhotoGroupCard key={`photos-${firstId}-${index}`} assets={item.data} />
          }

          if (item.type === 'spending') {
            const spendingKey =
              item.data.kind === 'receipt' ? `receipt-${item.data.receipt.id}` : `card-${item.data.transaction.id}`
            return <SpendingRow key={spendingKey} spending={item.data} />
          }

          // Movement session
          return (
            <OverlandMovementCard
              key={`movement-${item.data.id}-${index}`}
              session={item.data}
              onClick={handleMovementClick}
            />
          )
        })}
      </div>

      {/* 移動セッション詳細シート */}
      {selectedMovement && (
        <MovementDetailSheet
          session={selectedMovement}
          open={!!selectedMovement}
          onOpenChange={handleMovementSheetChange}
        />
      )}

      {/* メモ入力シート */}
      <NoteInputSheet
        open={noteSheetOpen}
        onOpenChange={setNoteSheetOpen}
        initialTimestamp={noteSheetTimestamp}
        visitId={noteSheetVisitId ?? undefined}
        episodeId={noteSheetEpisodeId ?? undefined}
        title={noteSheetTitle}
        onCreated={(note) => {
          handleNoteCreated(note)
          if (noteSheetVisitId != null) {
            void fetchVisits(format(selectedDate, 'yyyy-MM-dd'))
          }
        }}
      />

      {/* FAB: メモ追加ボタン */}
      {hasFullAccess && (
        <Fab icon={NotebookPen} onClick={handleOpenNoteSheet} className="bg-amber-500 hover:bg-amber-600 text-white" />
      )}
    </div>
  )
}
